import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  GAIN_MAX, GAIN_MIN,
  getGain, getSpeed, nextSpeed, setGain, setSpeed,
  type SpeedValue,
} from '../lib/audioPrefs'
import { useAudioBoost } from '../lib/useAudioBoost'

interface AudioPlayerProps {
  src: string
  testId?: string
  /**
   * Base id for the speed/volume controls (testids `speed-{id}` and `volume-{id}`).
   * Intentionally NOT prefixed with `audio-` — `RecordingsPage.test.tsx`
   * matches `getAllByTestId(/^audio-/)`, so any new control with that
   * prefix would inflate the count and break the existing assertion.
   */
  controlsTestId?: string
}

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return '0:00'
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

function formatGain(g: number): string {
  return `${Math.round(g * 100)}%`
}

function VolumeIcon({ gain }: { gain: number }): React.ReactElement {
  if (gain === 0) {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </svg>
    )
  }
  if (gain <= 1) {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      </svg>
    )
  }
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  )
}

export function AudioPlayer({ src, testId, controlsTestId }: AudioPlayerProps): React.ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [current, setCurrent] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // Lazy initial state: read persisted prefs once on first render.
  // Defaults (1× speed, 100% gain) are baked into audioPrefs so a fresh
  // user gets no AudioContext until they actively boost something.
  const [speed, setSpeedState] = useState<SpeedValue>(() => getSpeed())
  const [gain, setGainState] = useState<number>(() => getGain())
  // Popover is rendered via createPortal into document.body so it can't
  // be clipped by an ancestor with `overflow: hidden` (table cells, scroll
  // containers). Position is computed once on open from the button's
  // viewport rect; we close on scroll/resize rather than reposition because
  // listening across every scrollable ancestor + RAF tracking is more code
  // than this UI deserves. `popoverPos !== null` is the canonical "open" flag.
  const volumeBtnRef = useRef<HTMLButtonElement>(null)
  const volumeContentRef = useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null)
  const volumeOpen = popoverPos !== null

  // Mirror speed into a ref so the listeners effect (which mounts ONCE)
  // can read the current value inside onLoaded without re-subscribing.
  // Re-subscribing on every speed change would race events that fire in
  // the gap between removeEventListener and addEventListener.
  const speedRef = useRef(speed)
  useEffect(() => {
    speedRef.current = speed
    const a = audioRef.current
    if (a) a.playbackRate = speed
  }, [speed])

  useAudioBoost(audioRef, gain)

  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const onLoaded = (): void => {
      setDuration(a.duration || 0)
      // Some browsers reset playbackRate when loading new src; reapply.
      a.playbackRate = speedRef.current
    }
    const onTime = (): void => setCurrent(a.currentTime || 0)
    const onPlay = (): void => setPlaying(true)
    // pause/ended must clear `loading` too — `playing` event won't fire after
    // pause-during-buffer, so without this the spinner would stick.
    const onPause = (): void => {
      setPlaying(false)
      setLoading(false)
    }
    const onEnded = (): void => {
      setPlaying(false)
      setLoading(false)
      setCurrent(0)
    }
    const onWaiting = (): void => setLoading(true)
    const onPlaying = (): void => setLoading(false)
    const onError = (): void => {
      setPlaying(false)
      setLoading(false)
      setError(true)
    }
    // `loadstart` fires on every new src load — resets stale error state
    // when the user navigates between recordings without unmounting.
    const onLoadStart = (): void => setError(false)
    a.addEventListener('loadedmetadata', onLoaded)
    a.addEventListener('durationchange', onLoaded)
    a.addEventListener('timeupdate', onTime)
    a.addEventListener('play', onPlay)
    a.addEventListener('pause', onPause)
    a.addEventListener('ended', onEnded)
    a.addEventListener('waiting', onWaiting)
    a.addEventListener('playing', onPlaying)
    a.addEventListener('error', onError)
    a.addEventListener('loadstart', onLoadStart)
    return () => {
      a.removeEventListener('loadedmetadata', onLoaded)
      a.removeEventListener('durationchange', onLoaded)
      a.removeEventListener('timeupdate', onTime)
      a.removeEventListener('play', onPlay)
      a.removeEventListener('pause', onPause)
      a.removeEventListener('ended', onEnded)
      a.removeEventListener('waiting', onWaiting)
      a.removeEventListener('playing', onPlaying)
      a.removeEventListener('error', onError)
      a.removeEventListener('loadstart', onLoadStart)
    }
  }, [])

  // Close volume popover on outside click / Escape / scroll / resize.
  // Outside click checks BOTH the button and the portal'd content because
  // they're in different DOM subtrees.
  useEffect(() => {
    if (!volumeOpen) return
    const close = (): void => setPopoverPos(null)
    const onDoc = (e: MouseEvent): void => {
      const t = e.target as Node
      if (volumeBtnRef.current?.contains(t)) return
      if (volumeContentRef.current?.contains(t)) return
      close()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    // capture: true catches scrolls in every nested scroll container
    // (table wrappers, modal bodies) — `scroll` doesn't bubble.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [volumeOpen])

  const toggle = (): void => {
    const a = audioRef.current
    if (!a) return
    if (a.paused) {
      // AbortError ("play() interrupted by pause()") is a benign race between
      // a Play click and a follow-up Pause while the media is still loading
      // — swallow only that. Anything else (NotSupportedError on broken
      // codec, NotAllowedError from autoplay policy) is rethrown so the
      // global unhandled-rejection handler still ships it to Sentry.
      a.play().catch(err => {
        const name = err instanceof Error ? err.name : 'unknown'
        if (name !== 'AbortError') throw err
        if (import.meta.env.DEV) console.warn('[AudioPlayer] play interrupted:', err)
      })
    } else {
      a.pause()
    }
  }

  // Drag-to-seek: pointerdown captures the pointer so the slider keeps
  // tracking even if the cursor leaves the bar. Single click still works
  // — pointerdown+up at the same x. seekRectRef caches the bar's rect at
  // pointerdown so pointermove (which fires per pixel — 100+/sec on a
  // fast drag) doesn't pay for a forced layout read each event.
  const seekRectRef = useRef<DOMRect | null>(null)

  const seekTo = (clientX: number): void => {
    const a = audioRef.current
    const rect = seekRectRef.current
    if (!a || !duration || !rect) return
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const next = pct * duration
    // Skip if the change is below `timeupdate` granularity — saves a
    // flood of seeking/timeupdate events + re-renders during slow drags.
    if (Math.abs(next - a.currentTime) < 0.05) return
    a.currentTime = next
  }

  const onSeekPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    seekRectRef.current = e.currentTarget.getBoundingClientRect()
    seekTo(e.clientX)
  }

  const onSeekPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!seekRectRef.current) return
    seekTo(e.clientX)
  }

  const onSeekPointerEnd = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!seekRectRef.current) return
    seekRectRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  const cycleSpeed = (): void => {
    const next = nextSpeed(speed)
    setSpeedState(next)
    setSpeed(next)
  }

  const onGainChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = Number(e.target.value) / 100
    setGainState(v)
    setGain(v)
  }

  const toggleVolume = (): void => {
    if (volumeOpen) {
      setPopoverPos(null)
      return
    }
    const rect = volumeBtnRef.current?.getBoundingClientRect()
    // Right-aligned with the button; 4px gap below it. If the rect is
    // unavailable (jsdom), open at viewport origin — popover still renders
    // and tests can interact with it.
    setPopoverPos({
      top: rect ? rect.bottom + 4 : 4,
      right: rect ? window.innerWidth - rect.right : 0,
    })
  }

  const pct = duration > 0 ? (current / duration) * 100 : 0
  const speedTestId = controlsTestId ? `speed-${controlsTestId}` : undefined
  const volumeBtnTestId = controlsTestId ? `volume-${controlsTestId}` : undefined
  const volumeSliderTestId = controlsTestId ? `volume-slider-${controlsTestId}` : undefined

  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800/60 px-2 py-1 min-w-[260px] max-w-sm">
      <audio ref={audioRef} src={src} preload="metadata" data-testid={testId} />
      <button
        type="button"
        onClick={toggle}
        disabled={error}
        aria-label={error ? 'Не удалось загрузить запись' : playing ? 'Пауза' : 'Играть'}
        className={`flex-shrink-0 w-7 h-7 rounded-full text-white flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
          error ? 'bg-red-500' : 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700'
        }`}
      >
        {error ? (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ) : loading ? (
          <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
            <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : playing ? (
          <svg className="w-3 h-3" viewBox="0 0 8 10" fill="currentColor">
            <rect x="0" y="0" width="2.5" height="10" rx="0.5" />
            <rect x="5.5" y="0" width="2.5" height="10" rx="0.5" />
          </svg>
        ) : (
          <svg className="w-3 h-3 translate-x-[1px]" viewBox="0 0 8 10" fill="currentColor">
            <path d="M0 0v10l8-5z" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={cycleSpeed}
        aria-label={`Скорость ${speed}×`}
        data-testid={speedTestId}
        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[11px] font-mono font-medium tabular-nums text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors min-w-[34px]"
      >
        {speed}×
      </button>
      <div
        onPointerDown={onSeekPointerDown}
        onPointerMove={onSeekPointerMove}
        onPointerUp={onSeekPointerEnd}
        onPointerCancel={onSeekPointerEnd}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={current}
        tabIndex={0}
        className="flex-1 h-1.5 rounded-full bg-gray-300 dark:bg-gray-700 cursor-pointer relative group touch-none select-none"
      >
        <div
          className="h-full rounded-full bg-emerald-500 transition-[width] duration-75"
          style={{ width: `${pct}%` }}
        />
      </div>
      <button
        ref={volumeBtnRef}
        type="button"
        onClick={toggleVolume}
        aria-label={`Громкость ${formatGain(gain)}`}
        aria-expanded={volumeOpen}
        data-testid={volumeBtnTestId}
        className={`flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
          gain > 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-300'
        }`}
      >
        <VolumeIcon gain={gain} />
      </button>
      {volumeOpen && popoverPos && createPortal(
        <div
          ref={volumeContentRef}
          role="dialog"
          aria-label="Регулировка громкости"
          style={{ position: 'fixed', top: popoverPos.top, right: popoverPos.right }}
          className="z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-44"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Громкость
            </span>
            <span className={`text-xs font-mono tabular-nums ${
              gain > 1 ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-gray-700 dark:text-gray-300'
            }`}>
              {formatGain(gain)}
            </span>
          </div>
          <input
            type="range"
            min={GAIN_MIN * 100}
            max={GAIN_MAX * 100}
            step={5}
            value={Math.round(gain * 100)}
            onChange={onGainChange}
            data-testid={volumeSliderTestId}
            className="w-full accent-emerald-500"
            aria-label="Регулировка громкости"
          />
          <div className="flex justify-between text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">
            <span>0</span>
            <span>100</span>
            <span>300</span>
          </div>
          {gain > 1 && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 leading-snug">
              Усиление выше 100% — программный буст с компрессором, может вносить искажения.
            </p>
          )}
        </div>,
        document.body,
      )}
      <span className="flex-shrink-0 text-[11px] font-mono tabular-nums text-gray-600 dark:text-gray-400">
        {formatTime(current)}/{formatTime(duration)}
      </span>
    </div>
  )
}
